import React from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Organization, OrganizationUpdate } from '../../generated/api/types.gen.js';
import { zOrganizationUpdate } from '../../generated/api/zod.gen.js';
import {
  getOrganizationOptions,
  getOrganizationQueryKey,
  listOrganizationsQueryKey,
  updateOrganizationMutation,
} from '../../generated/api/@tanstack/react-query.gen.js';
import { Badge, Card, QueryBoundary, useToast } from '../../components/ui/index.js';
import {
  Form,
  MultiSelectFormField,
  SelectFormField,
  SubmitButton,
  SwitchFormField,
  TextFormField,
  useZodForm,
} from '../../components/form/index.js';
import { FormPage } from '../../components/page/FormPage.js';
import { Can } from '../../components/auth/Can.js';
import { PAGE_HELP } from '../../help/index.js';
import { ORGANIZATION_FIELD_HELP } from '../../help/organizations-fields.js';
import { orgPlanBadgeVariant, orgStatusBadgeVariant } from './org-badges.js';

const PLAN_OPTIONS: { value: NonNullable<OrganizationUpdate['plan']>; label: string }[] = [
  { value: 'free', label: 'Free' },
  { value: 'standard', label: 'Standard' },
  { value: 'enterprise', label: 'Enterprise' },
];
const STATUS_OPTIONS: { value: NonNullable<OrganizationUpdate['status']>; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
  { value: 'archived', label: 'Archived' },
];

// Industry modules known to the platform; `core` is always on.
const MODULE_OPTIONS = [
  { value: 'core', label: 'Core', description: 'Always enabled and cannot be removed.', disabled: true },
  { value: 'finance', label: 'Finance', description: 'Trading, strategies, portfolios and market data.' },
];

const FEATURE_META: Record<string, { name: string; description: string }> = {
  'events-stream': {
    name: 'Conversation event streaming',
    description: 'Real-time message and agent events in conversations.',
  },
  agentic_orchestrator: {
    name: 'Agentic orchestrator',
    description: 'Sends non-trivial conversation turns to the orchestrator, with tool calls streamed.',
  },
};

const sentenceCase = (value: string) => value.charAt(0).toUpperCase() + value.slice(1);

function pick<T extends string>(options: { value: T }[], value: string): T | undefined {
  return options.find((option) => option.value === value)?.value;
}

interface OrganizationFormProps {
  org: Organization;
}

const OrganizationForm: React.FC<OrganizationFormProps> = ({ org }) => {
  const queryClient = useQueryClient();
  const { notify } = useToast();
  const update = useMutation(updateOrganizationMutation());

  const form = useZodForm(zOrganizationUpdate, {
    defaultValues: {
      name: org.name,
      logoUrl: org.logoUrl ?? '',
      // A plan or status outside the editable set (the system organization's) is left unset, so it is not changed.
      plan: pick(PLAN_OPTIONS, org.plan),
      status: pick(STATUS_OPTIONS, org.status),
      features: org.features,
      modules: org.modules,
    },
  });

  const save = async (body: OrganizationUpdate) => {
    await update.mutateAsync({ path: { id: org.id }, body });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getOrganizationQueryKey({ path: { id: org.id } }) }),
      queryClient.invalidateQueries({ queryKey: listOrganizationsQueryKey() }),
    ]);
    form.reset(form.getValues());
    notify('Organization saved');
  };

  const featureKeys = Object.keys(org.features);

  return (
    <Form form={form} onSubmit={save} guardUnsaved>
      <TextFormField name="name" label="Name" help={ORGANIZATION_FIELD_HELP.name} required />
      <TextFormField
        name="logoUrl"
        label="Logo address"
        type="url"
        placeholder="https://…"
        help={ORGANIZATION_FIELD_HELP.logoUrl}
        emptyAs={null}
      />
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <SelectFormField name="plan" label="Plan" options={PLAN_OPTIONS} help={ORGANIZATION_FIELD_HELP.plan} />
        <SelectFormField name="status" label="Status" options={STATUS_OPTIONS} help={ORGANIZATION_FIELD_HELP.status} />
      </div>
      <MultiSelectFormField
        name="modules"
        label="Modules"
        options={MODULE_OPTIONS}
        description={ORGANIZATION_FIELD_HELP.modules}
      />

      <fieldset className="space-y-3">
        <legend className="text-sm font-medium text-heading">Feature flags</legend>
        {featureKeys.length === 0 ? (
          <p className="text-sm text-muted">No feature flags are configured for this organization.</p>
        ) : (
          featureKeys.map((key) => (
            <SwitchFormField
              key={key}
              name={`features.${key}`}
              label={FEATURE_META[key]?.name ?? key}
              description={FEATURE_META[key]?.description}
            />
          ))
        )}
      </fieldset>

      <Can policy="CanManageOrgs">
        <div>
          <SubmitButton>Save changes</SubmitButton>
        </div>
      </Can>
    </Form>
  );
};

export const OrganizationDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const orgId = Number(id);
  const query = useQuery({ ...getOrganizationOptions({ path: { id: orgId } }), enabled: !Number.isNaN(orgId) });
  const org = query.data?.organization;

  return (
    <FormPage
      title={org?.name ?? 'Organization'}
      subtitle={org ? `/${org.slug}` : undefined}
      helpContent={PAGE_HELP.organizations}
      backTo="/admin/organizations"
      backLabel="Organizations"
      aside={
        org && (
          <Card title="Overview">
            <dl className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <dt className="text-muted">Members</dt>
                <dd className="text-lg font-semibold text-heading">{org.memberCount}</dd>
              </div>
              <div>
                <dt className="text-muted">Teams</dt>
                <dd className="text-lg font-semibold text-heading">{org.teamCount}</dd>
              </div>
              <div className="col-span-2">
                <dt className="text-muted">Owner</dt>
                <dd className="truncate text-heading">{org.ownerName ?? 'No owner'}</dd>
              </div>
              <div>
                <dt className="mb-1 text-muted">Plan</dt>
                <dd>
                  <Badge variant={orgPlanBadgeVariant(org.plan)}>{sentenceCase(org.plan)}</Badge>
                </dd>
              </div>
              <div>
                <dt className="mb-1 text-muted">Status</dt>
                <dd>
                  <Badge variant={orgStatusBadgeVariant(org.status)} dot>
                    {sentenceCase(org.status)}
                  </Badge>
                </dd>
              </div>
            </dl>
          </Card>
        )
      }
    >
      <QueryBoundary query={query} label="organization">
        {(loaded) => <OrganizationForm key={loaded.organization.id} org={loaded.organization} />}
      </QueryBoundary>
    </FormPage>
  );
};
